import EfLink from 'elem/ui/ef-link';
import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import styled from './index.module.scss';
import { useReferralDispatch, useReferralStore } from '@/components/Referral/ReferralProvider';
import { copyText } from '@/components/Referral/util';
import Button from '@/components/UI/Button';
import Input from '@/components/UI/Input';
import { message } from '@/components/UI/Message';
import EfModal from '@/components/UI/Modal';
import { FaceBookIconNew, TwitterIconNew } from '@/components/UI/svg/Referral';
import { sendInviteEmail } from '@/service/referral';
import { loginUser } from '@/utils/cookie';
import gtm from '@/utils/gtm';
import efBridge from '@/utils/JSBridge';
import { useGlobal } from '@/utils/useGlobal';

export type ReferHandle = {
    show: () => void;
};

const reg = /^[A-Za-z\d]+([.%~`$^*&(){}<>?"+=!_\\-]*[A-Za-z\d-])*@[a-zA-Z\d-]{1,30}\.[a-zA-Z\d.%#~`$^*&(){}<>?"+=!_\\-]{1,30}$/;

const Refer = (_props: object, ref: ForwardedRef<ReferHandle>) => {
    const { useLang, isMobile, isApp } = useGlobal();
    const { locale } = useGlobal().useRouter();
    const { t } = useLang('referral');
    const { t: sharedT } = useLang('shared');
    const [show, setShow] = useState(false);
    const { shareInfo, referralInfo, pageUsing } = useReferralStore();
    const dispatch = useReferralDispatch();
    const [copyValue, setCopyValue] = useState('');
    const [sendEmail, setSendEmail] = useState('');

    const shareText = {
        twitter: `${t('refer.share_text')}\n${copyValue}`,
    };

    const handleShareInfo = () => {
        efBridge.callHandler('shareMessage', {
            title: t('refer.share_text'),
            url: copyValue,
        });
    };

    const handleShow = () => {
        let shareUrlPath = 'referral-rewards';
        if (pageUsing === 'dp3') {
            shareUrlPath = 'referral-rewards-delta-pro-3-portable-power-station';
        }
        if (pageUsing === 'd3ms') {
            shareUrlPath = 'referee-delta-3-max-series';
        }
        dispatch({
            type: 'getShareInfo',
            payload: {
                data: {
                    planId: referralInfo?.planId,
                    shareUrl: `${location.origin}/${locale}/${shareUrlPath}`,
                },
                locale,
            },
        });
        setShow(true);
    };

    const handleSendMsg = () => {
        if (!referralInfo || !shareInfo) return;
        const { email } = loginUser().user;
        if (email && email === sendEmail.trim()) return message.error(t('refer.thesameemail'));
        sendInviteEmail(
            {
                planId: referralInfo?.planId,
                shareEmail: sendEmail.trim(),
                shareId: shareInfo?.shareId,
                shareUrl: copyValue,
            },
            locale,
        )
            .then((res) => {
                if (res?.code !== '0') return message.error(res?.message);
                message.success(sharedT('my.site_account_profile_save_success'));
                setSendEmail('');
            })
            .catch((err) => {
                console.log(err);
            });
    };

    useEffect(() => {
        if (!shareInfo) return;
        let path = 'referral-rewards';
        if (pageUsing === 'dp3') path = 'referral-rewards-delta-pro-3-portable-power-station';
        if (pageUsing === 'd3ms') path = 'referee-delta-3-max-series';
        setCopyValue(`${location.origin}/${locale}/${path}?inviteCode=${shareInfo?.inviteCode}`);
    }, [shareInfo, pageUsing, locale]);

    useImperativeHandle(ref, () => ({
        show: () => handleShow(),
    }));

    return (
        <EfModal
            width={560}
            visible={show}
            open={show}
            className={`${styled.refer_modal}`}
            onCancel={() => setShow(false)}
            getContainer={true}
            maskClosable={isMobile}
            centered={!isMobile || isApp}
        >
            <div className={styled.refer_modal}>
                <section className="refer-title">
                    <h5 className="title">{t('refer.pop_refer_title')}</h5>
                    <p className="desc">{t('refer.pop_refer_desc')}</p>
                </section>
                <section className="share-link">
                    <h5 className="title">{t('refer.pop_share_link_title')}</h5>
                    <div className="copy_content">
                        <Input value={copyValue} disabled />
                        <Button
                            className="refer_modal_btn"
                            onClick={() => {
                                copyText(copyValue, sharedT, locale);
                                try {
                                    gtm.push({
                                        event: 'uaEvent',
                                        event_name: 'referral_button',
                                        position: t('refer.pop_refer_title'),
                                        button_name: t('refer.pop_share_link_btn'),
                                    });
                                } catch (e) {
                                    console.log(e);
                                }
                            }}
                        >
                            {t('refer.pop_share_link_btn')}
                        </Button>
                    </div>
                    <p className="line">
                        <span>{t('refer.pop_other')}</span>
                    </p>
                </section>
                <section className="email-directly">
                    <h5 className="title">{t('refer.pop_email_directly_title')}</h5>
                    <div className="copy_content1">
                        <Input value={sendEmail} onChange={(e: any) => setSendEmail(e.target.value)} placeholder={t('refer.pop_input_placeholder')} />
                        <div className="send_email">
                            <Button
                                className="refer_modal_btn"
                                onClick={() => {
                                    handleSendMsg();
                                    try {
                                        gtm.push({
                                            event: 'uaEvent',
                                            event_name: 'referral_button',
                                            position: t('refer.pop_refer_title'),
                                            button_name: t('refer.pop_email_directly_btn'),
                                        });
                                    } catch (e) {
                                        console.log(e);
                                    }
                                }}
                                disabled={!reg.test(sendEmail)}
                            >
                                {t('refer.pop_email_directly_btn')}
                            </Button>
                        </div>
                    </div>
                    <p className="line">
                        <span>{locale === 'jp' ? t('refer.pop_other1') : t('refer.pop_other')}</span>
                    </p>
                </section>
                <div className="tools_icon">
                    {isApp ? (
                        <Button className="refer_modal_btn" onClick={handleShareInfo}>
                            {t('refer.pop_share_conmon_share')}
                        </Button>
                    ) : (
                        <>
                            <EfLink
                                href={`https://www.facebook.com/sharer/sharer.php?u=${copyValue}`}
                                dataLayer={{
                                    event: 'uaEvent',
                                    event_name: 'share',
                                    method: 'Facebook',
                                    item_id: 'Refer A Friend Now!',
                                }}
                                target="_blank"
                            >
                                <FaceBookIconNew />
                            </EfLink>
                            <EfLink
                                href={`https://twitter.com/intent/tweet?text=${shareText.twitter}`}
                                dataLayer={{
                                    event: 'uaEvent',
                                    event_name: 'share',
                                    method: 'Twitter',
                                    item_id: 'Refer A Friend Now!',
                                }}
                                target="_blank"
                            >
                                <TwitterIconNew />
                            </EfLink>
                        </>
                    )}
                </div>
                <p className="tips">{t('refer.share_footer_info')}</p>
            </div>
        </EfModal>
    );
};

export default forwardRef<ReferHandle, object>(Refer);
